import { ApolloClient, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getCarlitoHubAccessToken } from '@carlito/hub-react';
import { createUploadLink } from 'apollo-upload-client';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { getConciergeConfig } from '../../config';
import { readJwt } from '../persistance';
import { autoRefetchWhenInactive } from './auto-refetch';

const { link: networkStatusLink, ...networkStatusNotifier } = createNetworkStatusNotifier();
export const useApolloNetworkStatus = networkStatusNotifier.useApolloNetworkStatus;

// This link (instead of a standard HTTP link)
// allows us to upload files via GraphQL
const uploadHttpLink = createUploadLink({
  //  uri: `https://api.concierge-hotel.com/graphql`,
  uri: `${getConciergeConfig().apiUri}/graphql`,
});

// An array of GraphQL Operation names where the `authorization`
// shouldn't be sent along
const UNAUTHENTICATED_OPERATIONS = [
  'LoginMutation',
  'RegisterMutation',
  'ForgotPassword',
  'LoginAsGuest',
  'GetHotelBySubdomain',
  'GetAppAuthImages',
  'GetHotelCapabilitesBySubdomain',
  'SearchHotelByName',
];

const authLink = setContext(async (request, { headers }) => {
  const shouldAuthenticate = !UNAUTHENTICATED_OPERATIONS.includes(request.operationName ?? '');
  // get the authentication token from local storage if it exists
  const token = await readJwt();
  const hubAccessToken = getCarlitoHubAccessToken();

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      ...(shouldAuthenticate ? { authorization: token ? `Bearer ${token}` : '' } : {}),
      ...(hubAccessToken ? { 'carlito-hub-access-token': hubAccessToken } : {}),
    },
  };
});

type ApolloCachElem = {
  // Has form "Hotel:123"
  __ref?: string;
};

const getCacheElemId = (elem: ApolloCachElem) => elem.__ref?.split(':')[1] || '';

const client = new ApolloClient({
  link: networkStatusLink.concat(authLink.concat(uploadHttpLink as any) as any) as any,
  cache: new InMemoryCache({
    typePolicies: {
      Hotel: {
        fields: {
          surroundingInformationCategories: {
            keyArgs: [],
            merge(existing: ApolloCachElem[], incoming: ApolloCachElem[]) {
              const newArr = [
                ...(existing ?? []),
                ...incoming.filter((el) => {
                  const elId = getCacheElemId(el);
                  return !existing?.some((e) => getCacheElemId(e) === elId);
                }),
              ];

              return newArr;
            },
          },
        },
      },
    },
  }),
});

// (window as any).apolloClient = client;

autoRefetchWhenInactive({ client });

export default client;
